import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { appPath } from '../utils/appRouting';
import { 
  CheckCircle,
  Loader2,
  ArrowRight
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { getApiBaseUrl } from '../utils/backendBaseUrl';

const API = getApiBaseUrl();

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentUser } = useAuth();
  const { refreshSubscription } = useSubscription();
  const [confirming, setConfirming] = useState(true);
  const [confirmed, setConfirmed] = useState(false);

  const sessionId = useMemo(() => searchParams.get('session_id'), [searchParams]);

  useEffect(() => {
    const run = async () => {
      if (!sessionId) {
        setConfirming(false);
        setConfirmed(false);
        return;
      }

      if (!currentUser) {
        setConfirming(false);
        setConfirmed(false);
        return;
      }

      try {
        setConfirming(true);
        const token = await currentUser.getIdToken();
        await axios.get(`${API}/payments/checkout/status/${sessionId}`, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 25000
        });
        await refreshSubscription();
        setConfirmed(true);
      } catch (error) {
        console.error('Errore conferma pagamento:', error);
        const detail = error?.response?.data?.detail;
        toast.error(detail || 'Pagamento completato ma conferma fallita');
        setConfirmed(false);
      } finally {
        setConfirming(false);
      }
    };

    run();
  }, [currentUser, refreshSubscription, sessionId]);

  return (
    <div className="h-[100dvh] overflow-y-auto bg-gradient-to-b from-green-50 to-white flex items-center justify-center p-4">
      <div className="max-w-md w-full ds-panel p-8 text-center">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          {confirming ? (
            <Loader2 className="w-8 h-8 text-green-600 animate-spin" />
          ) : (
            <CheckCircle className="w-8 h-8 text-green-600" />
          )}
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-4">
          {confirming ? 'Conferma pagamento in corso...' : 'Pagamento completato'}
        </h2>
        <p className="text-gray-600 mb-6">
          {!sessionId
            ? 'Manca il session_id: torna alle impostazioni e aggiorna la pagina.'
            : !currentUser
              ? 'Devi effettuare il login per vedere il tuo piano aggiornato.'
              : confirmed
                ? 'Piano/crediti aggiornati. Puoi continuare.'
                : "Se non vedi l'aggiornamento subito, attendi qualche secondo e ricarica le impostazioni."}
        </p>
        <div className="space-y-3">
          {!currentUser ? (
            <Button
              onClick={() => navigate(appPath('/login'))}
              className="w-full ds-btn-gradient"
            >
              Accedi
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={() => navigate(appPath('/dashboard'))}
              className="w-full ds-btn-gradient"
            >
              Vai alla Dashboard
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}
          <Link
            to={appPath('/settings')}
            className="block text-sm text-gray-500 hover:text-gray-700"
          >
            Vai alle impostazioni
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
